import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { toast } from "sonner";

import { switchWorkspace } from "../../../lib/switchWorkspace";
import { LAUNCH_FAILURE_COPY } from "./launchFailureCopy";
import { LoadingSpinner } from "../../../components/ui/loadingSpinner/loadingSpinner";
import { useAuth } from "../../../contexts/authContext";
import { useOptroLaunchResolve } from "../../../hooks/useOptroLaunchResolve";

/** Optro "Run Automated Test" landing page (SOX-97513). Behind RequireAuth only,
 * NOT RequireAuthAndAuditEnabled — the active workspace may lack audit while the
 * Optro-connected one has it. All decisions are the backend's; this page acts on
 * status codes. */
export const OptroLaunchPage = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { workspace } = useAuth();
  const { resolve, failure } = useOptroLaunchResolve();
  const [switchingTo, setSwitchingTo] = useState<string | null>(null);
  const requested = useRef(false);

  useEffect(() => {
    if (!token || requested.current || !workspace) {
      return;
    }
    requested.current = true;
    void resolve(token).then((outcome) => {
      if (!outcome) {
        return;
      }
      if (outcome.kind === "resolved") {
        navigate(outcome.path, { replace: true });
        return;
      }
      // 409: membership already verified server-side. The shared helper owns
      // the hard-reload contract; the intent survives because the backend
      // only clears it on an actual resolve.
      setSwitchingTo(outcome.workspaceName);
      switchWorkspace(outcome.workspaceId, window.location.pathname);
    });
  }, [token, workspace, resolve, navigate]);

  // Failures render inside the app shell (sidebar and all) so the user can
  // navigate onward — this token route itself has no nav by design. A
  // workspace without audit access would be bounced off that route by
  // RequireAuthAndAuditEnabled before the copy ever rendered, so those users
  // get the app-wide error pattern instead: a toast, on their own home.
  useEffect(() => {
    if (!failure) {
      return;
    }
    if (workspace && !workspace.auditEnabled) {
      toast.error(LAUNCH_FAILURE_COPY[failure], { duration: 10000 });
      navigate("/", { replace: true });
      return;
    }
    navigate("/audit/optro/launch-failed", {
      replace: true,
      state: { failure },
    });
  }, [failure, navigate, workspace]);

  return (
    <div className="flex h-screen flex-col items-center justify-center gap-3">
      <LoadingSpinner />
      <p className="text-sm text-stone-500">
        {switchingTo
          ? `Switching to ${switchingTo}…`
          : "Opening your test in Midship…"}
      </p>
    </div>
  );
};
